import React, { useEffect, useState, useContext } from 'react'

import LoadingContext from '../../context/Loading/Loading'
import AuthService from '../../utilities/services/auth.service'
import UserContext from '../../context/User/User'
import PackageDetailsContext from '../../context/PackageDetails/PackageDetails'

import {
  getPackage,
  getPackageStats,
  updatePackageBlockDirectUrl,
  updatePackageVisibility,
} from '../../utilities/api'

import useSafeAsync from '../../utilities/useSafeAsync'

import { ORGANIZATION_PERMISSION_SLUGS } from '@blocktickets/shared-utils'
import { checkPermission } from '../../utilities/helpers'

import { Cards } from './Cards'
import { NoPermissionsContainer } from '../NoPermissionsContainer'
import { PageLoadingContainer } from '../PageLoadingContainer'

const PUBLISHED_PACKAGE_STATUSES = ['published', 'on_sale', 'complete']

const isPublishedStatus = (eventPackage) =>
  PUBLISHED_PACKAGE_STATUSES.includes(eventPackage?.status)

export default function PackageDashboardWrapper({ packageId }) {
  const safeSetState = useSafeAsync()
  const { isLoading, showLoading, hideLoading } = useContext(LoadingContext)

  const { orgPermissions } = useContext(UserContext)

  const {
    eventPackage: contextPackage,
    updateIsPackageOnsale,
    updateIsPackagePublished,
    updatePackage,
    updateIsPackageSoldout,
    updateHasPackageEnded,
    hasPackageEnded,
  } = useContext(PackageDetailsContext)

  const { getPermissions } = AuthService

  const orgPermissionsReady = orgPermissions?.length > 0
  const hasPermission =
    orgPermissionsReady &&
    checkPermission(
      getPermissions(),
      ORGANIZATION_PERMISSION_SLUGS.SEASON_PACKAGES_MANAGEMENT
    )

  const [isVisible, setIsVisible] = useState(true)
  const [blockDirectUrl, setBlockDirectUrl] = useState(false)
  const [savingBlockDirectUrl, setSavingBlockDirectUrl] = useState(false)

  const [eventPackage, setEventPackage] = useState()

  const [stats, setStats] = useState()

  useEffect(() => {
    if (!orgPermissionsReady) return
    if (!hasPermission) {
      hideLoading()
      return
    }

    showLoading()
    loadPackage()
  }, [orgPermissionsReady, hasPermission, packageId])

  const loadPackage = async () => {
    try {
      const packageData = await getPackage(packageId)

      safeSetState(() => {
        setEventPackage(packageData.data)
        setIsVisible(packageData.data?.isVisible)
        setBlockDirectUrl(Boolean(packageData.data?.blockDirectUrl))
        // context updates
        updateIsPackageOnsale(packageData?.data)
        updateHasPackageEnded(packageData?.data)
        updateIsPackageSoldout(packageData?.data)
        updateIsPackagePublished(packageData?.data)
      })

      const statsData = await getPackageStats(packageId)

      safeSetState(() => {
        setStats(statsData?.data)
      })
    } catch (err) {
      console.error(err)
    } finally {
      hideLoading()
    }
  }

  const isPublished =
    isPublishedStatus(eventPackage) ||
    (contextPackage?.uuid &&
      contextPackage.uuid === eventPackage?.uuid &&
      isPublishedStatus(contextPackage))

  const handleChange = (_, nextIsVisible) => {
    if (!isPublished) return
    setIsVisible(nextIsVisible)
    const nextPackage = {
      ...eventPackage,
      isVisible: nextIsVisible,
      blockDirectUrl,
    }
    setEventPackage(nextPackage)
    updatePackage(nextPackage)
    updatePackageVisibility({
      data: {
        id: eventPackage?.uuid,
        isVisible: nextIsVisible,
      },
    })
  }

  const handleBlockDirectUrl = async (_, nextBlockDirectUrl) => {
    if (savingBlockDirectUrl) return
    const previousBlockDirectUrl = blockDirectUrl
    const previousPackage = eventPackage
    setSavingBlockDirectUrl(true)
    setBlockDirectUrl(nextBlockDirectUrl)
    const nextPackage = {
      ...eventPackage,
      isVisible,
      blockDirectUrl: nextBlockDirectUrl,
    }
    setEventPackage(nextPackage)
    updatePackage(nextPackage)
    try {
      await updatePackageBlockDirectUrl({
        data: {
          id: eventPackage?.uuid,
          blockDirectUrl: nextBlockDirectUrl,
        },
      })
    } catch (err) {
      console.error(err)
      setBlockDirectUrl(previousBlockDirectUrl)
      setEventPackage(previousPackage)
      updatePackage(previousPackage)
    } finally {
      setSavingBlockDirectUrl(false)
    }
  }

  if (!orgPermissionsReady) {
    return <PageLoadingContainer />
  }

  if (!hasPermission) {
    return <NoPermissionsContainer />
  }

  if (isLoading) {
    return <PageLoadingContainer />
  }

  return (
    <>
      <div className="position-relative">
        <div
          className="max-width-wrapper"
          data-testid="package-dashboard-wrapper-section"
        >
          <header className="section-header section-heading">
            <h1>Dashboard</h1>
          </header>
          <Cards
            eventPackage={eventPackage}
            stats={stats}
            isVisible={isVisible}
            isPublished={isPublished}
            handleChange={handleChange}
            blockDirectUrl={blockDirectUrl}
            handleBlockDirectUrl={handleBlockDirectUrl}
            savingBlockDirectUrl={savingBlockDirectUrl}
            isDisabled={hasPackageEnded}
          />
        </div>
      </div>
    </>
  )
}
